import {
  AdaptiveDialogDescription,
  AdaptiveDialogTitle,
} from "@/lib/components/ui/adaptive-dialog/adaptive-dialog";
import Button from "@/lib/components/ui/button/button";
import Text from "@/lib/components/ui/text/text";
import type { ImperativeDialogAction } from "./imperative-dialog.types";
import type { ReactNode } from "react";

function imperativeDialogActionButtonChildren(
  action: ImperativeDialogAction,
) {
  return (
    <>
      {action.startIcon ?? null}
      {action.label}
      {action.endIcon ?? null}
    </>
  );
}

export type ImperativeDialogContentProps = {
  cover: ReactNode | null;
  title: ReactNode;
  action?: ImperativeDialogAction;
  eyebrow?: ReactNode;
  children?: ReactNode;
  description?: ReactNode;
  onActionClick: () => void;
  secondaryAction?: ImperativeDialogAction;
  onSecondaryActionClick: () => void;
};

export function ImperativeDialogContent({
  cover,
  title,
  action,
  eyebrow,
  children,
  description,
  onActionClick,
  secondaryAction,
  onSecondaryActionClick,
}: ImperativeDialogContentProps) {
  const hasCover = cover !== null;
  const hasFooter = Boolean(action ?? secondaryAction);

  return (
    <section
      className={
        hasCover ? "min-h-112 md:grid md:grid-cols-[18rem_1fr]" : "min-h-112"
      }
    >
      {hasCover ? cover : null}
      <div className="flex flex-col justify-between gap-6 p-6">
        <div className="space-y-6">
          {children ? (
            <>
              <div className="space-y-2">
                <AdaptiveDialogTitle>{title}</AdaptiveDialogTitle>
              </div>
              {children}
            </>
          ) : (
            <>
              {eyebrow ? (
                <Text
                  variant="label"
                  className="text-foreground-pressed tracking-wide uppercase"
                >
                  {eyebrow}
                </Text>
              ) : null}
              <div className="space-y-2">
                <AdaptiveDialogTitle>{title}</AdaptiveDialogTitle>
                {description ? (
                  <AdaptiveDialogDescription>
                    {description}
                  </AdaptiveDialogDescription>
                ) : null}
              </div>
            </>
          )}
        </div>

        {hasFooter ? (
          <div className="flex gap-3">
            {secondaryAction ? (
              <Button
                type="button"
                variant={secondaryAction.variant ?? "secondary"}
                onClick={function handleSecondaryClick() {
                  onSecondaryActionClick();
                }}
                className="flex-1"
              >
                {imperativeDialogActionButtonChildren(secondaryAction)}
              </Button>
            ) : null}
            {action ? (
              <Button
                type="button"
                variant={action.variant ?? "default"}
                onClick={function handlePrimaryClick() {
                  onActionClick();
                }}
                className="flex-1"
              >
                {imperativeDialogActionButtonChildren(action)}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}
